import * as React from 'react';
import { useState } from 'react';
import {
    ShowBase,
    NumberField,
    useShowContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import { Navigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { QuoteAside } from './QuoteAside';
import { ContractorAside } from './ContractorAside';
import { QuoteDriveExplorer } from './QuoteDriveExplorer';
import { RelatedPeopleTable } from '@/modules/projects/RelatedPeopleTable';
import { Status } from '@/components/misc/Status';
import { ResourceNotFound } from '@/components/misc/ResourceNotFound';
import {
    showMainSx,
    showPageRowSx,
} from '@/components/layout/showPageLayout';
import { Quote } from '@/types';
import { useBrowserTitle } from '@/utils/useBrowserTitle';
import { authedFetch } from '@/providers/loginWithEmail/dataProvider';
import {
    propertyDescriptionType,
    projectDeckType,
    roofType,
    roofingSystem,
    defaultTypeOfWork,
    quoteStatuses,
} from '@/root/defaultConfiguration';

type QuoteShowTab = 'property' | 'steep' | 'legacy' | 'attachments';

export const QuoteShow = () => (
    <ShowBase queryOptions={{ meta: { includes: 'projects' } }}>
        <QuoteShowContent />
    </ShowBase>
);

function LegacyDataSection({ estimationData }: { estimationData?: Record<string, unknown> | null }) {
    const legacyData = estimationData?.legacy_data;
    if (!legacyData || typeof legacyData !== 'object') {
        return (
            <Typography variant="body1" color="text.secondary">
                No legacy data
            </Typography>
        );
    }
    const entries = Object.entries(legacyData as Record<string, unknown>).filter(([, value]) => {
        if (value == null) return false;
        const text = String(value).trim();
        return text !== '' && text.toLowerCase() !== 'n/a';
    });
    if (entries.length === 0) {
        return (
            <Typography variant="body1" color="text.secondary">
                No legacy data
            </Typography>
        );
    }
    return (
        <Box>
            {entries.map(([key, value]) => (
                <Typography key={key} variant="body1" component="div">
                    <strong>{key}</strong>: {String(value)}
                </Typography>
            ))}
        </Box>
    );
}

function regenerateDescriptionErrorMessage(data: {
    detail?: unknown;
    errors?: { root?: unknown };
}): string {
    const err = data?.detail ?? data?.errors;
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && err !== null && 'root' in err) {
        const root = (err as { root: unknown }).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return 'Failed to regenerate quote name';
}

const QuoteShowContent = () => {
    const { record, isPending, error } = useShowContext<Quote>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [tab, setTab] = useState<QuoteShowTab>('attachments');
    const [isRegeneratingName, setRegeneratingName] = useState(false);
    useBrowserTitle(`${record?.description} - Quote`);

    if (isPending) return null;
    if (error || !record) {
        return (
            <ResourceNotFound
                resourceLabel="Quote"
                listPath="/quotes"
                listLabel="Back to quotes"
            />
        );
    }

    if (record.status === quoteStatuses.pending_finish_quoting) {
        return <Navigate to={`/quotes/${record.id}`} replace />;
    }

    const canRegenerateName = !record.is_legacy;

    const handleRegenerateDescription = async () => {
        setRegeneratingName(true);
        try {
            const res = await authedFetch(
                `/quotes/${record.id}/regenerate-description`,
                { method: 'POST' },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                notify(regenerateDescriptionErrorMessage(data), { type: 'error' });
                return;
            }
            notify(
                data.folder_renamed
                    ? 'Quote name updated and OneDrive folder renamed'
                    : 'Quote name updated',
                { type: 'success' },
            );
            refresh();
        } catch {
            notify('Failed to regenerate quote name', { type: 'error' });
        } finally {
            setRegeneratingName(false);
        }
    };

    return (
        <Box sx={showPageRowSx}>
            <QuoteAside />
            <Box sx={showMainSx}>
                <Card>
                    <CardContent>
                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                            gap={1}
                        >
                            <Box flex="1" minWidth={0} sx={{ mb: 2, ml: { xs: 0, sm: 2 } }}>
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={1}
                                    flexWrap="wrap"
                                    sx={{ mb: 0.5 }}
                                >
                                    <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
                                        {record.description}
                                    </Typography>
                                    {canRegenerateName && (
                                        <Box position="relative">
                                            <IconButton
                                                size="small"
                                                aria-label="Regenerate quote name"
                                                title="Regenerate quote name"
                                                onClick={handleRegenerateDescription}
                                                disabled={isRegeneratingName}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                            {isRegeneratingName && (
                                                <CircularProgress
                                                    size={20}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        marginTop: '-10px',
                                                        marginLeft: '-10px',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {record.is_legacy && (
                                        <Chip label="Legacy" size="small" color="warning" />
                                    )}
                                </Box>
                                <Typography variant="body1" component="div" sx={{ wordBreak: 'break-word' }}>
                                    {record.address}
                                </Typography>
                                <Typography variant="body2" component="div">
                                    {record.city} - {record.state}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                    <strong>Zip Code</strong>: {record.project?.zip_code}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                    <strong>Total Area</strong>: {record.total_area}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                    <strong>Total Edge Distance</strong>: {record.total_edge_distance}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                    <strong>Total Parapet Distance</strong>: {record.total_parapet_distance}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                    <strong>Total price</strong>: <NumberField source="price" options={{
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2
                                    }} />
                                </Typography>
                                {record.creator_name && (
                                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                        <strong>Created by</strong>: {record.creator_name}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' } }}>
                                <Status status={record.status} />
                            </Box>
                        </Box>

                        <Tabs
                            value={tab}
                            onChange={(_, value: QuoteShowTab) => setTab(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}
                        >
                            <Tab label="Property Description" value="property" />
                            <Tab label="Description Steep" value="steep" />
                            {record.is_legacy && (
                                <Tab label="Legacy Data" value="legacy" />
                            )}
                            <Tab label="Attachments" value="attachments" />
                        </Tabs>

                        <Box ml={{ xs: 0, sm: 2 }} pt={2} sx={{ mb: 1 }}>
                            {tab === 'property' && (
                                <Box>
                                    <Typography variant="body1" component="div">
                                        <strong>Property Description</strong>: {record.property_description ? propertyDescriptionType[record.property_description] : 'Not selected'}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Type of Work</strong>: {record.type_of_work ? defaultTypeOfWork[record.type_of_work] : 'Not selected'}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Roof Type</strong>: {record.roof_type ? roofType[record.roof_type] : 'Not selected'}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Desired Steep Slope</strong>: <span>{record.roof_type && record.desired_steep_slope ? roofingSystem[record.roof_type][record.desired_steep_slope] : 'Not selected'}</span>
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Desired Low Slope</strong>: {record.roof_type && record.desired_low_slope ? roofingSystem[record.roof_type][record.desired_low_slope] : 'Not selected'}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Folio Number</strong>: {record.project?.folio_number}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Year Built</strong>: {record.project?.year_built}
                                    </Typography>
                                </Box>
                            )}

                            {tab === 'steep' && (
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1 }}>Description Steep</Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Deck Type</strong>: {record.steep_deck_type ? projectDeckType[record.steep_deck_type] : 'Not selected'}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Area</strong>: {record.steep_area}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Edge Distance</strong>: {record.steep_edge_distance}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Ridge Distance</strong>: {record.steep_ridge_distance}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Valley</strong>: {record.steep_valley}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Parapet Distance</strong>: {record.steep_parapet_distance}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Steep Ridge Vent</strong>: {record.steep_ridge_vent}
                                    </Typography>

                                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Description Flat</Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Flat Deck Type</strong>: {record.flat_deck_type ? projectDeckType[record.flat_deck_type] : 'Not selected'}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Flat Area</strong>: {record.flat_area}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Flat Edge Distance</strong>: {record.flat_edge_distance}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        <strong>Flat Parapet Distance</strong>: {record.flat_parapet_distance}
                                    </Typography>
                                </Box>
                            )}

                            {tab === 'legacy' && record.is_legacy && (
                                <LegacyDataSection
                                    estimationData={
                                        record.estimation_data as Record<string, unknown> | null | undefined
                                    }
                                />
                            )}

                            {tab === 'attachments' && (
                                <Box sx={{ ml: { xs: 0, sm: 2 }, mt: 2, mb: 2 }}>
                                    <QuoteDriveExplorer
                                        quoteId={Number(record.id)}
                                    />
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
            <ContractorAside />
        </Box>
    );
};
